import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import {
  ApiConnectionItemCreate,
  ApiConnectionItemDelete,
  ApiConnectionItemInitialState,
  ApiConnectionItemLoad,
  ApiConnectionItemUpdate,
} from './api-connection-item.actions';
import { ApiConnectionModel } from '../models/api-connection.model';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { NotificationService } from '../../../../../common/services/notification/notification.service';
import { ApiConnectionItemService } from './api-connection-item.service';
import { NavigateUserService } from '../../../../services/navigate-user.service';
import { CustomHttpErrorResponse, Error } from '../../../../../common/constants/error';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface ApiConnectionItemStateModel {
  apiConnection: ApiConnectionModel;
  loaded: boolean;
}

const initialState: ApiConnectionItemStateModel = {
  apiConnection: new ApiConnectionModel(),
  loaded: false,
};

@State<ApiConnectionItemStateModel>({
  name: 'apiConnectionItem',
  defaults: initialState,
})
@Injectable()
export class ApiConnectionItemState {
  constructor(
    private apiConnectionItemService: ApiConnectionItemService,
    private navigateUserService: NavigateUserService,
    private notificationService: NotificationService,
  ) {}

  @Selector()
  static getState(state: ApiConnectionItemStateModel): ApiConnectionItemStateModel {
    return state;
  }

  @Selector()
  static getApiConnection(state: ApiConnectionItemStateModel): ApiConnectionModel {
    return state.apiConnection;
  }

  @Selector()
  static getApiConnectionId(state: ApiConnectionItemStateModel): number {
    return state.apiConnection.id;
  }

  @Selector()
  static getApiConnectionName(state: ApiConnectionItemStateModel): string {
    return state.apiConnection.name;
  }

  @Selector()
  static getLoaded(state: ApiConnectionItemStateModel): boolean {
    return state.loaded;
  }

  @Action(ApiConnectionItemLoad)
  getApiConnectionDetails(
    ctx: StateContext<ApiConnectionItemStateModel>,
    { payload }: ApiConnectionItemLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.apiConnectionItemService.getApiConnectionDetails(payload).pipe(
      take(1),
      map((apiConnectionDetails: ApiConnectionModel) => {
        ctx.patchState({
          apiConnection: new ApiConnectionModel(apiConnectionDetails),
          loaded: true,
        });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ApiConnectionItemCreate)
  createNewApiConnection(
    ctx: StateContext<ApiConnectionItemStateModel>,
    { payload }: ApiConnectionItemCreate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.apiConnectionItemService.updateApiConnection(payload).pipe(
      take(1),
      map(() => {
        this.notificationService.success('api.apiConnection.success.create');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/api-connections');
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ApiConnectionItemUpdate)
  updateApiConnection(
    ctx: StateContext<ApiConnectionItemStateModel>,
    { payload }: ApiConnectionItemUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.apiConnectionItemService.updateApiConnection(payload).pipe(
      take(1),
      map((apiConnectionDetails: ApiConnectionModel) => {
        this.notificationService.success('api.apiConnection.success.update');
        ctx.patchState({ apiConnection: apiConnectionDetails, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ApiConnectionItemDelete)
  deleteApiConnection(
    ctx: StateContext<ApiConnectionItemStateModel>,
    { payload }: ApiConnectionItemDelete,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | Observable<void>> {
    return this.apiConnectionItemService.deleteApiConnection(payload.apiConnectionId).pipe(
      take(1),
      map(() => {
        payload.dialog.close();
        this.notificationService.success('api.apiConnection.success.delete');
        this.navigateUserService.navigateUserToAbsolutePath('/dashboard/administration/api-connections');
        return ctx.dispatch(new ApiConnectionItemInitialState());
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(ApiConnectionItemInitialState)
  setInitialState(ctx: StateContext<ApiConnectionItemStateModel>): void {
    ctx.setState(initialState);
  }
}
